import React, { useState, useEffect, useRef } from 'react';
import ProteinViewer from './ProteinViewer';

interface PredictionResult {
  residue_number: number;
  residue_type: string;
  exosite_probability: number;
  predicted_exosite: number;
  confidence_level: string;
}

interface PredictionResponse {
  success: boolean;
  pdb_id: string;
  chain: string;
  sequence_length: number;
  n_predicted_exosites: number;
  exosite_percentage: number;
  mean_probability: number;
  max_probability: number;
  predictions: PredictionResult[];
  model_info: any;
  processing_time_seconds?: number;
  pdb_content?: string;
  chains_available?: string[];
  error?: string;
}

interface ExositePredictionProps {
  isMobile: boolean;
  isSmallMobile: boolean;
}

const ExositePrediction: React.FC<ExositePredictionProps> = ({ isMobile, isSmallMobile }) => {
  const [inputMode, setInputMode] = useState<'file' | 'pdb-id'>('pdb-id');
  const [file, setFile] = useState<File | null>(null);
  const [pdbId, setPdbId] = useState<string>('');
  const [proteinName, setProteinName] = useState<string>('');
  const [predicting, setPredicting] = useState<boolean>(false);
  const [checkingAvailability, setCheckingAvailability] = useState<boolean>(false);
  const [pdbAvailable, setPdbAvailable] = useState<boolean | null>(null);
  const [result, setResult] = useState<PredictionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [show3DViewer, setShow3DViewer] = useState(false);
  const [confidenceFilter, setConfidenceFilter] = useState<'ALL' | 'HIGH+MEDIUM' | 'HIGH'>('ALL');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.pdb')) {
        setFile(droppedFile);
        setError(null);
      } else {
        setError('Please upload a PDB file (.pdb)');
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.endsWith('.pdb')) {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Please upload a PDB file (.pdb)');
      }
    }
  };

  // Check PDB availability in STINGRDB2
  const checkPdbAvailability = async (pdbIdToCheck: string) => {
    if (pdbIdToCheck.length !== 4) {
      setPdbAvailable(null);
      return;
    }

    setCheckingAvailability(true);
    try {
      const response = await fetch(`/predict/check-pdb-availability/${pdbIdToCheck.toUpperCase()}`);
      const data = await response.json();
      setPdbAvailable(data.available);
    } catch (err) {
      setPdbAvailable(null);
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Handle PDB ID input change
  const handlePdbIdChange = (value: string) => {
    const cleanValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
    setPdbId(cleanValue);
    setPdbAvailable(null);

    // Check availability when 4 characters entered
    if (cleanValue.length === 4) {
      checkPdbAvailability(cleanValue);
    }
  };

  const handlePredict = async () => {
    setError(null);
    setPredicting(true);
    setResult(null);
    setShow3DViewer(false);

    try {
      let response;

      if (inputMode === 'pdb-id') {
        // PDB ID mode - use Paper4 family-specific model
        if (!pdbId || pdbId.length !== 4) {
          setError('Please enter a valid 4-character PDB ID');
          setPredicting(false);
          return;
        }

        const params = new URLSearchParams();
        if (proteinName) {
          params.append('protein_name', proteinName);
        }

        const url = `/predict/exosite-by-pdb-id/${pdbId.toUpperCase()}${params.toString() ? '?' + params.toString() : ''}`;
        response = await fetch(url);
      } else {
        // File upload mode - use existing ESM2 model
        if (!file) {
          setError('Please upload a PDB file');
          setPredicting(false);
          return;
        }

        const formData = new FormData();
        formData.append('pdb_file', file);
        if (pdbId) {
          formData.append('pdb_id', pdbId);
        }

        response = await fetch('/predict/exosite-pdb', {
          method: 'POST',
          body: formData
        });
      }

      const data = await response.json();

      if (data.success) {
        setResult(data);
        // Only show 3D viewer if we have PDB content
        setShow3DViewer(!!data.pdb_content);
      } else {
        setError(data.error || 'Prediction failed');
      }
    } catch (err) {
      setError(`Network error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setPredicting(false);
    }
  };

  const clearAll = () => {
    setFile(null);
    setPdbId('');
    setProteinName('');
    setPdbAvailable(null);
    setResult(null);
    setError(null);
    setShow3DViewer(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadResults = () => {
    if (!result) return;

    const headers = ['Residue_Number', 'Residue_Type', 'Exosite_Probability', 'Predicted_Exosite', 'Confidence_Level'];
    const rows = result.predictions.map(p => [
      p.residue_number,
      p.residue_type,
      p.exosite_probability.toFixed(4),
      p.predicted_exosite,
      p.confidence_level
    ]);

    const csvContent = [
      `# STINGExoFind Prediction Results`,
      `# PDB ID: ${result.pdb_id}`,
      `# Chain: ${result.chain}`,
      `# Sequence Length: ${result.sequence_length}`,
      `# Predicted Exosites: ${result.n_predicted_exosites} (${result.exosite_percentage}%)`,
      `#`,
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stingexofind_${result.pdb_id}_predictions.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Filter predictions by confidence level
  const getFilteredPredictions = () => {
    if (!result) return [];

    return result.predictions.filter(p => {
      if (p.predicted_exosite !== 1) return false;

      if (confidenceFilter === 'HIGH') {
        return p.confidence_level === 'HIGH';
      } else if (confidenceFilter === 'HIGH+MEDIUM') {
        return p.confidence_level === 'HIGH' || p.confidence_level === 'MEDIUM';
      }
      return true; // ALL
    });
  };

  // Get exosites by confidence level for 3D visualization
  const getExositesByConfidence = (): { high: number[]; medium: number[]; low: number[] } => {
    if (!result) return { high: [], medium: [], low: [] };

    const filtered = getFilteredPredictions();

    return {
      high: filtered.filter(p => p.confidence_level === 'HIGH').map(p => p.residue_number),
      medium: filtered.filter(p => p.confidence_level === 'MEDIUM').map(p => p.residue_number),
      low: filtered.filter(p => p.confidence_level === 'LOW').map(p => p.residue_number)
    };
  };

  const cardStyle: React.CSSProperties = {
    background: '#ffffff',
    borderRadius: '12px',
    padding: isMobile ? '16px' : '24px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
    marginBottom: '16px'
  };

  const buttonStyle: React.CSSProperties = {
    padding: isMobile ? '10px 16px' : '12px 24px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: isMobile ? '14px' : '16px',
    fontWeight: '600',
    transition: 'all 0.2s'
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    color: 'white'
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: '#f3f4f6',
    color: '#374151'
  };

  return (
    <div style={{
      maxWidth: show3DViewer && result ? '100%' : '1200px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '24px'
    }}>
      {/* Header */}
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1 style={{
            fontSize: isMobile ? '28px' : '36px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '12px'
          }}>
            STINGExoFind
          </h1>
          <p style={{
            fontSize: isMobile ? '14px' : '16px',
            color: '#6b7280',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            Predict exosite residues from protein structures
          </p>
        </div>
      </div>

      {/* Input Section */}
      {!show3DViewer && (
        <div style={cardStyle}>
          <h2 style={{ fontSize: isMobile ? '20px' : '24px', marginBottom: '16px', fontWeight: '600' }}>
            Predict Exosites
          </h2>

          {/* Input Mode Toggle */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '20px',
            background: '#f3f4f6',
            padding: '4px',
            borderRadius: '10px'
          }}>
            <button
              onClick={() => setInputMode('pdb-id')}
              style={{
                flex: 1,
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                background: inputMode === 'pdb-id' ? 'white' : 'transparent',
                color: inputMode === 'pdb-id' ? '#3b82f6' : '#6b7280',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                boxShadow: inputMode === 'pdb-id' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              PDB ID Lookup
            </button>
            <button
              onClick={() => setInputMode('file')}
              style={{
                flex: 1,
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                background: inputMode === 'file' ? 'white' : 'transparent',
                color: inputMode === 'file' ? '#3b82f6' : '#6b7280',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                boxShadow: inputMode === 'file' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              Upload PDB File
            </button>
          </div>

          {/* PDB ID Mode */}
          {inputMode === 'pdb-id' && (
            <>
              {/* PDB ID Input */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                  PDB ID <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={pdbId}
                    onChange={(e) => handlePdbIdChange(e.target.value)}
                    placeholder="e.g., 1PPB, 2ERK, 3EGH"
                    disabled={predicting}
                    maxLength={4}
                    style={{
                      width: '100%',
                      padding: '12px 48px 12px 12px',
                      borderRadius: '8px',
                      border: `2px solid ${pdbAvailable === true ? '#10b981' : pdbAvailable === false ? '#ef4444' : '#e5e7eb'}`,
                      fontSize: '16px',
                      fontFamily: 'monospace',
                      letterSpacing: '2px',
                      outline: 'none',
                      textTransform: 'uppercase'
                    }}
                  />
                  {/* Availability Indicator */}
                  {pdbId.length === 4 && (
                    <div style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: checkingAvailability ? '#9ca3af' : pdbAvailable === true ? '#10b981' : pdbAvailable === false ? '#ef4444' : '#9ca3af'
                    }} />
                  )}
                </div>
                {pdbAvailable === false && (
                  <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                    Not in database - try uploading file
                  </div>
                )}
              </div>

              {/* Protein Name (optional) */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                  Protein Name (optional)
                </label>
                <input
                  type="text"
                  value={proteinName}
                  onChange={(e) => setProteinName(e.target.value)}
                  placeholder="e.g., Thrombin, Kinase, MMP-9"
                  disabled={predicting}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>
            </>
          )}

          {/* File Upload Mode */}
          {inputMode === 'file' && (
            <>
              {/* PDB ID (optional) */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                  PDB ID (optional)
                </label>
                <input
                  type="text"
                  value={pdbId}
                  onChange={(e) => setPdbId(e.target.value.toUpperCase())}
                  placeholder="e.g., 1PPB"
                  disabled={predicting}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    fontSize: '16px',
                    outline: 'none'
                  }}
                />
              </div>

              {/* File Upload Area */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${dragActive ? '#3b82f6' : file ? '#10b981' : '#d1d5db'}`,
                  borderRadius: '12px',
                  padding: isMobile ? '32px 16px' : '48px 24px',
                  textAlign: 'center',
                  background: dragActive ? '#eff6ff' : file ? '#f0fdf4' : '#f9fafb',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  marginBottom: '16px'
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdb"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />

                {file ? (
                  <>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      color: 'white',
                      fontSize: '24px',
                      fontWeight: 'bold'
                    }}>✓</div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#10b981',
                      marginBottom: '4px'
                    }}>{file.name}</div>
                    <div style={{
                      fontSize: '13px',
                      color: '#6b7280'
                    }}>{(file.size / 1024).toFixed(1)} KB</div>
                  </>
                ) : (
                  <>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '8px',
                      background: '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      color: '#6b7280'
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                    </div>
                    <div style={{
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '4px'
                    }}>
                      {dragActive ? 'Drop file here' : 'Drop or click to upload'}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#9ca3af'
                    }}>.pdb format</div>
                  </>
                )}
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={handlePredict}
              disabled={predicting || (inputMode === 'file' && !file) || (inputMode === 'pdb-id' && pdbId.length !== 4)}
              style={{
                ...primaryButtonStyle,
                opacity: (predicting || (inputMode === 'file' && !file) || (inputMode === 'pdb-id' && pdbId.length !== 4)) ? 0.5 : 1,
                flex: isMobile ? '1 1 100%' : '0 0 auto'
              }}
            >
              {predicting ? 'Analyzing Structure...' : 'Predict Exosites'}
            </button>

            {(file || pdbId) && (
              <button
                onClick={clearAll}
                disabled={predicting}
                style={{
                  ...secondaryButtonStyle,
                  flex: isMobile ? '1 1 100%' : '0 0 auto'
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{
          ...cardStyle,
          background: '#fef2f2',
          border: '2px solid #fecaca',
          color: '#dc2626'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* 3D Visualization Results */}
      {show3DViewer && result && result.success && result.pdb_content && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 400px',
          gap: '16px'
        }}>
          {/* 3D Viewer */}
          <div style={{
            ...cardStyle,
            padding: 0,
            overflow: 'hidden',
            minHeight: isMobile ? '400px' : '600px'
          }}>
            <div style={{
              padding: '16px',
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '20px' }}>
                  {result.pdb_id} - 3D Structure
                </h2>
                <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '4px' }}>
                  {result.n_predicted_exosites} exosites predicted ({result.exosite_percentage}%)
                </div>
              </div>
              <button
                onClick={() => setShow3DViewer(false)}
                style={{
                  ...secondaryButtonStyle,
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
              >
                New Prediction
              </button>
            </div>

            {/* ProteinViewer Component with custom PDB content */}
            <div style={{ height: isMobile ? '400px' : '600px' }}>
              <CustomPDBViewer
                pdbContent={result.pdb_content}
                exositesByConfidence={getExositesByConfidence()}
                pdbId={result.pdb_id}
              />
            </div>
          </div>

          {/* Results Panel */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* Summary Stats */}
            <div style={cardStyle}>
              <h3 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: '600' }}>
                Prediction Summary
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px'
              }}>
                <StatCard
                  label="Chains"
                  value={result.chain}
                  color="#3b82f6"
                />
                <StatCard
                  label="Residues"
                  value={result.sequence_length}
                  color="#6b7280"
                />
                <StatCard
                  label="Exosites"
                  value={result.n_predicted_exosites}
                  color="#10b981"
                />
                <StatCard
                  label="Coverage"
                  value={`${result.exosite_percentage}%`}
                  color="#8b5cf6"
                />
              </div>

              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: '#f9fafb',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#6b7280'
              }}>
                Processing: {result.processing_time_seconds}s
              </div>
            </div>

            {/* Predicted Exosite Residues */}
            {result.n_predicted_exosites > 0 && (
              <div style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                    Predicted Exosite Residues
                  </h3>
                  <select
                    value={confidenceFilter}
                    onChange={(e) => setConfidenceFilter(e.target.value as any)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '2px solid #e5e7eb',
                      fontSize: '13px',
                      fontWeight: '600',
                      background: 'white',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                  >
                    <option value="ALL">All Confidence</option>
                    <option value="HIGH+MEDIUM">High + Medium</option>
                    <option value="HIGH">High Only</option>
                  </select>
                </div>
                <div style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  fontSize: '13px'
                }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse'
                  }}>
                    <thead style={{
                      position: 'sticky',
                      top: 0,
                      background: '#f3f4f6',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      <tr>
                        <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: '600' }}>Pos</th>
                        <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: '600' }}>AA</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: '600' }}>Prob</th>
                        <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: '600' }}>Conf</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredPredictions().map((pred: any, idx: number) => {
                          const confColor =
                            pred.confidence_level === 'HIGH' ? '#10b981' :
                            pred.confidence_level === 'MEDIUM' ? '#f59e0b' : '#6b7280';

                          return (
                            <tr key={idx} style={{
                              borderBottom: '1px solid #f3f4f6',
                              background: idx % 2 === 0 ? 'white' : '#f9fafb'
                            }}>
                              <td style={{ padding: '8px 4px', fontWeight: '500' }}>
                                {pred.residue_number}
                              </td>
                              <td style={{ padding: '8px 4px', fontFamily: 'monospace', fontWeight: '600' }}>
                                {pred.residue_type}
                              </td>
                              <td style={{ padding: '8px 4px', textAlign: 'right', fontFamily: 'monospace' }}>
                                {(pred.exosite_probability * 100).toFixed(1)}%
                              </td>
                              <td style={{ padding: '8px 4px' }}>
                                <span style={{
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  background: confColor + '20',
                                  color: confColor,
                                  fontSize: '11px',
                                  fontWeight: '600'
                                }}>
                                  {pred.confidence_level}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Download Button */}
            <button
              onClick={downloadResults}
              style={{
                ...primaryButtonStyle,
                width: '100%'
              }}
            >
              Download CSV
            </button>

            {/* Legend */}
            <div style={cardStyle}>
              <h3 style={{ fontSize: '16px', marginBottom: '12px', fontWeight: '600' }}>
                Color Legend
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '16px', height: '16px', background: '#dc2626', borderRadius: '4px' }}></div>
                  <span>High Confidence</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '16px', height: '16px', background: '#ea580c', borderRadius: '4px' }}></div>
                  <span>Medium Confidence</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '16px', height: '16px', background: '#78716c', borderRadius: '4px' }}></div>
                  <span>Low Confidence</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '16px', height: '16px', background: '#8b5cf6', borderRadius: '4px' }}></div>
                  <span>Protein Backbone</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

// Helper component for stats
const StatCard: React.FC<{ label: string; value: string | number; color: string }> = ({ label, value, color }) => (
  <div style={{
    padding: '12px',
    background: '#f9fafb',
    borderRadius: '8px',
    borderLeft: `4px solid ${color}`
  }}>
    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>{value}</div>
  </div>
);

// Custom PDB Viewer component that accepts PDB content directly
const CustomPDBViewer: React.FC<{
  pdbContent: string;
  exositesByConfidence: { high: number[]; medium: number[]; low: number[] };
  pdbId: string
}> = ({
  pdbContent,
  exositesByConfidence,
  pdbId
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const [viewer, setViewer] = useState<any>(null);

  useEffect(() => {
    // Load 3Dmol.js
    const script = document.createElement('script');
    script.src = 'https://3dmol.csb.pitt.edu/build/3Dmol-min.js';
    script.async = true;
    script.onload = () => {
      initializeViewer();
    };
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    // Update viewer when confidence filter changes
    if (viewer && window.$3Dmol) {
      updateViewer();
    }
  }, [exositesByConfidence]);

  const updateViewer = () => {
    if (!viewer) return;

    // Reset all styles
    viewer.setStyle({}, { cartoon: { color: '#8b5cf6' } });

    // Apply HIGH confidence (bright red)
    exositesByConfidence.high.forEach(resi => {
      viewer.setStyle({ resi }, {
        cartoon: { color: '#dc2626' },
        sphere: { color: '#dc2626', radius: 1.5 }
      });
    });

    // Apply MEDIUM confidence (orange)
    exositesByConfidence.medium.forEach(resi => {
      viewer.setStyle({ resi }, {
        cartoon: { color: '#ea580c' },
        sphere: { color: '#ea580c', radius: 1.5 }
      });
    });

    // Apply LOW confidence (gray-brown)
    exositesByConfidence.low.forEach(resi => {
      viewer.setStyle({ resi }, {
        cartoon: { color: '#78716c' },
        sphere: { color: '#78716c', radius: 1.2 }
      });
    });

    viewer.render();
  };

  const initializeViewer = () => {
    if (!viewerRef.current || !window.$3Dmol) return;

    const config = { backgroundColor: '#1e293b' };
    const v = window.$3Dmol.createViewer(viewerRef.current, config);

    // Add PDB content
    v.addModel(pdbContent, 'pdb');

    // Style protein
    v.setStyle({}, { cartoon: { color: '#8b5cf6' } });

    // Highlight exosites with different colors by confidence
    // HIGH confidence (bright red)
    exositesByConfidence.high.forEach(resi => {
      v.setStyle({ resi }, {
        cartoon: { color: '#dc2626' },
        sphere: { color: '#dc2626', radius: 1.5 }
      });
    });

    // MEDIUM confidence (orange)
    exositesByConfidence.medium.forEach(resi => {
      v.setStyle({ resi }, {
        cartoon: { color: '#ea580c' },
        sphere: { color: '#ea580c', radius: 1.5 }
      });
    });

    // LOW confidence (gray-brown)
    exositesByConfidence.low.forEach(resi => {
      v.setStyle({ resi }, {
        cartoon: { color: '#78716c' },
        sphere: { color: '#78716c', radius: 1.2 }
      });
    });

    v.zoomTo();
    v.render();
    v.zoom(0.8, 1000);

    setViewer(v);
  };

  return <div ref={viewerRef} style={{ width: '100%', height: '100%', position: 'relative' }} />;
};

export default ExositePrediction;
